import React from "react";

// --- default data (unique keys)
const defaultClimateRiskMatrixData = [
  // Physical risks
  {
    categoryCRM: "Physical risks",
    riskCRM: "Increased Frequency of Extreme Weather Events",
    potentialImpactsCRM:
      "Supply Chain Disruptions, Facility Damage, Increased Insurance Costs",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: false,
  },
  {
    categoryCRM: "Physical risks",
    riskCRM: "Rising Sea Levels",
    potentialImpactsCRM:
      "Property Damage, Need for Relocation, Increased Infrastructure Costs",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: false,
  },
  {
    categoryCRM: "Physical risks",
    riskCRM: "Changes in Precipitation Patterns",
    potentialImpactsCRM:
      "Water Scarcity, Operational Delays, Crop Yield Impacts",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: false,
  },
  {
    categoryCRM: "Physical risks",
    riskCRM: "Heatwaves or Increased temperature",
    potentialImpactsCRM:
      "Higher Energy Costs, Reduced Productivity, Equipment Overheating",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: false,
  },
  {
    categoryCRM: "Physical risks",
    riskCRM: "Others (specify)",
    potentialImpactsCRM: "",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: true,
  },

  // Transitional risks
  {
    categoryCRM: "Transitional risks",
    riskCRM: "Regulatory changes (e.g., carbon taxes, ESG requirements)",
    potentialImpactsCRM:
      "Increased compliance costs, penalties, adjustments in operations",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: false,
  },
  {
    categoryCRM: "Transitional risks",
    riskCRM: "Market shifts toward low-carbon products",
    potentialImpactsCRM:
      "Declining demand for carbon-intensive products, need for innovation",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: false,
  },
  {
    categoryCRM: "Transitional risks",
    riskCRM: "Technology advancements",
    potentialImpactsCRM:
      "Write-off of current assets and systems, capital investment in newer technology",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: false,
  },
  {
    categoryCRM: "Transitional risks",
    riskCRM: "Investor/consumer pressure for sustainability",
    potentialImpactsCRM:
      "Brand reputation risks, reduced investor interest, loss of market share",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: false,
  },
  {
    categoryCRM: "Transitional risks",
    riskCRM: "Others (specify)",
    potentialImpactsCRM: "",
    timePeriodCRM: "",
    applicableCRM: "",
    additionalDetailsCRM: "",
    isOtherRowCRM: true,
  },
];

const ClimateRiskMatrixTable = ({
  climateRiskMatrixData,
  setClimateRiskMatrixData,
}) => {
  // ---- styles (remove per-cell borders; outer frame + inner grid handle corners)
  const cell = {
    padding: "15px",
    textAlign: "left",
    verticalAlign: "top",
  };

  const header = {
    ...cell,
    backgroundColor: "#d3c9ce",
    fontWeight: "bold",
    textAlign: "center",
  };

  const categoryCell = {
    ...cell,
    backgroundColor: "#d3c9ce",
    fontWeight: 600,
    textAlign: "center",
    minWidth: 140,
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid gray",
    borderRadius: "6px",
    fontSize: "14px",
  };

  const textAreaStyle = { ...inputStyle, minHeight: 60, resize: "vertical" };

  // ---- helpers
  const handleChange = (idx, field, value) => {
    const updated = [...climateRiskMatrixData];
    updated[idx][field] = value;
    setClimateRiskMatrixData(updated);
  };

  // compute rowSpan for each category
  const categoryCounts = climateRiskMatrixData.reduce((acc, row) => {
    acc[row.categoryCRM] = (acc[row.categoryCRM] || 0) + 1;
    return acc;
  }, {});
  const firstIndexOfCategory = {};
  climateRiskMatrixData.forEach((row, idx) => {
    if (firstIndexOfCategory[row.categoryCRM] === undefined) {
      firstIndexOfCategory[row.categoryCRM] = idx;
    }
  });

  return (
    <div style={{ overflowX: "auto", marginTop: 20 }}>
      {/* table frame + perfect corners */}
      <style>{`
        .crmTableFrame{
          border: 1px solid #000;
          border-radius: 12px;
          overflow: hidden;
        }
        .crmTable{
          width: 100%;
          border-collapse: collapse;
        }
        .crmTable th,
        .crmTable td{
          border: 1px solid #000; /* inner grid lines */
          padding: 15px;
          vertical-align: top;
          text-align: left;
        }
        .crmTable thead th{
          background: #d3c9ce;
          font-weight: 700;
          text-align: center;
        }
        /* hide outer edges; frame draws outside border */
        .crmTable thead tr:first-child th{ border-top: 0; }
        .crmTable tr th:first-child,
        .crmTable tr td:first-child{ border-left: 0; }
        .crmTable tr th:last-child,
        .crmTable tr td:last-child{ border-right: 0; }
        .crmTable tbody tr:last-child td{ border-bottom: 0; }
      `}</style>

      <div className="crmTableFrame">
        <table className="crmTable">
          <thead>
            <tr>
              <th style={header}>Category</th>
              <th style={header}>Risk</th>
              <th style={header}>
                Potential Impacts
                <br />
                (pre-defined)
              </th>
              <th style={header}>
                Time period (short-
                <br />
                term / medium-term
                <br />/ long-term)
              </th>
              <th style={header}>
                Whether applicable
                <br />
                for your company
                <br />
                (Yes / No)
              </th>
              <th style={header}>Additional details</th>
            </tr>
          </thead>

          <tbody>
            {climateRiskMatrixData.map((row, idx) => {
              const showCategory =
                firstIndexOfCategory[row.categoryCRM] === idx;
              const span = categoryCounts[row.categoryCRM];

              return (
                <tr key={idx}>
                  {showCategory && (
                    <td style={categoryCell} rowSpan={span}>
                      {row.categoryCRM}
                    </td>
                  )}

                  {/* Risk */}
                  <td style={cell}>
                    {row.isOtherRowCRM ? (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "10px 12px",
                        }}
                      >
                        {row.riskCRM || "Others (specify)"}
                      </span>
                    ) : (
                      row.riskCRM
                    )}
                  </td>

                  {/* Potential Impacts */}
                  <td style={cell}>
                    {row.isOtherRowCRM ? (
                      <input
                        style={inputStyle}
                        placeholder="Enter data"
                        value={row.potentialImpactsCRM}
                        onChange={(e) =>
                          handleChange(
                            idx,
                            "potentialImpactsCRM",
                            e.target.value
                          )
                        }
                      />
                    ) : (
                      row.potentialImpactsCRM
                    )}
                  </td>

                  {/* Time period */}
                  <td style={cell}>
                    <input
                      style={inputStyle}
                      placeholder="Enter data"
                      value={row.timePeriodCRM}
                      onChange={(e) =>
                        handleChange(idx, "timePeriodCRM", e.target.value)
                      }
                    />
                  </td>

                  {/* Whether applicable for your company (Yes / No) */}
                  <td style={cell}>
                    <input
                      style={inputStyle}
                      placeholder="Enter data"
                      value={row.applicableCRM}
                      onChange={(e) =>
                        handleChange(idx, "applicableCRM", e.target.value)
                      }
                    />
                  </td>

                  {/* Additional details */}
                  <td style={cell}>
                    <input
                      style={inputStyle}
                      placeholder="Enter data"
                      value={row.additionalDetailsCRM}
                      onChange={(e) =>
                        handleChange(
                          idx,
                          "additionalDetailsCRM",
                          e.target.value
                        )
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClimateRiskMatrixTable;
